import { fetchWithAuth } from "../utils/fetch/FetchWithAuth";
import { prepareBaseGetListParams } from "../utils/fetch/PrepareBaseGetListParams";

export const RouteService = {
    listRoutes: (criteria) => {
        const params = prepareBaseGetListParams(criteria);
        // Dodatkowe parametry domenowe mogą być tu dodane (np. statuses)
        return fetchWithAuth(`/portal/routes?${params.toString()}`, { method: 'GET' });
    },
    listActiveRoutes: (criteria) => {
        const params = prepareBaseGetListParams(criteria);
        return fetchWithAuth(`/portal/routes/active?${params.toString()}`, { method: 'GET' });
    },
    getStatusList: () => {
        return fetchWithAuth('/portal/routes/status-list', { method: 'GET' });
    },
    getInactiveStatusList: () => {
        return fetchWithAuth('/portal/routes/inactive-status-list', { method: 'GET' });
    },
    getNotStartedStatusList: () => {
        return fetchWithAuth('/portal/routes/not-started-status-list', { method: 'GET' });
    },
    isPendingTaskExistsForLocation: (locationId) => {
        return fetchWithAuth(`/portal/routes/location/${locationId}/pending-task-exists`, { method: 'GET' });
    },
    isPendingTaskExistsForDevice: (deviceId) => {
        return fetchWithAuth(`/portal/routes/device/${deviceId}/pending-task-exists`, { method: 'GET' });
    },
    getRouteDetails: (routeId) => {
        return fetchWithAuth(`/portal/routes/${routeId}`, { method: 'GET' });
    },
    getLocationDetails: (locationId, routeId, waypointId) => {
        let url = routeId 
            ? `/portal/routes/locations/${locationId}/details?route_id=${routeId}`
            : `/portal/routes/locations/${locationId}/details`;
        if (waypointId) {
            if (url.includes('?')) {
                url += `&waypoint_id=${waypointId}`;
            } else {
                url += `?waypoint_id=${waypointId}`;
            }
        }
        return fetchWithAuth(url, { method: 'GET' });
    },
    rescheduleTasks: (taskIds) => {
        return fetchWithAuth('/portal/routes/tasks/reschedule', {
            method: 'POST',
            body: JSON.stringify(taskIds)
        });
    },
    deleteTasks: (routeId, locationId, taskIds) => {
        return fetchWithAuth(`/portal/routes/${routeId}/locations/${locationId}/tasks`, {
            method: 'DELETE',
            body: JSON.stringify(taskIds)
        });
    },
    changeTechnicianForTasks: (routeId, locationId, technicianId, taskIds, scheduledDate = null) => {
        let url = `/portal/routes/${routeId}/locations/${locationId}/tasks/change-technician/${technicianId}`;
        if (scheduledDate) {
            const params = new URLSearchParams();
            params.append('scheduled_date', scheduledDate);
            url += `?${params.toString()}`;
        }
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify(taskIds)
        });
    },
    getSupplyRequests: (criteria, routeId, groupBy) => {
        const params = prepareBaseGetListParams(criteria);
        if (routeId) {
            params.append('route_id', routeId);
        }
    
        const endpoint = groupBy === 'LOCATION' 
            ? '/portal/routes/supply-requests/by-location'
            : '/portal/routes/supply-requests/by-product';
        return fetchWithAuth(`${endpoint}?${params.toString()}`, { method: 'GET' });
    },
    getLocationEvents: (locationId, routeId) => {
        const url = routeId 
            ? `/portal/routes/locations/${locationId}/events?route_id=${routeId}`
            : `/portal/routes/locations/${locationId}/events`;
        return fetchWithAuth(url, { method: 'GET' });
    },
    addTasksToRoute: (routeId, locationId, request) => {
        return fetchWithAuth(`/portal/routes/${routeId}/locations/${locationId}/tasks`, {
            method: 'POST',
            body: JSON.stringify(request)
        });
    },
    addLocationTasksToRouteBulk: (routeId, locationId, taskIds) => {
        return fetchWithAuth(`/portal/routes/${routeId}/locations/${locationId}/tasks/bulk`, {
            method: 'POST',
            body: JSON.stringify(taskIds)
        });
    },
    isPendingTaskExistsForUser: (userId) => {
        return fetchWithAuth(`/portal/routes/user/${userId}/pending-task-exists`, { method: 'GET' });
    },
    getLocationsWithTasksWithoutRoute: (criteria) => {
        const params = prepareBaseGetListParams(criteria);
        return fetchWithAuth(`/portal/routes/locations/with-tasks-without-route?${params.toString()}`, { method: 'GET' });
    },
    getTechniciansList: (currentTechnicianId) => {
        const query = currentTechnicianId ? `?current=${currentTechnicianId}` : '';
        return fetchWithAuth(`/portal/routes/technicians-list${query}`, { method: 'GET' });
    },
    getPlannedRoutesList: (technicianId) => {
        const queryParams = new URLSearchParams();
        if (technicianId) {
            queryParams.append('technician_id', technicianId);
        }
        const queryString = queryParams.toString();
        const url = `/portal/routes/planned-list${queryString ? `?${queryString}` : ''}`;
        return fetchWithAuth(url, { method: 'GET' });
    },
    changeLocationTechnician: (routeId, locationId, waypointId, technicianId, options = {}) => {
        let url = `/portal/routes/${routeId}/locations/${locationId}/technician`;
        if (waypointId) {
            url += `?waypoint_id=${waypointId}`;
        }
        const payload = {
            technicianId,
            mergeRoutes: Boolean(options.mergeRoutes),
            scheduleShiftDays: Number.isInteger(options.scheduleShiftDays) && options.scheduleShiftDays > 0
                ? options.scheduleShiftDays
                : 0
        };

        return fetchWithAuth(url, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    },
    changeRouteTechnician: (routeId, technicianId, options = {}) => {
        const payload = {
            technicianId,
            mergeRoutes: Boolean(options.mergeRoutes),
            scheduleShiftDays: Number.isInteger(options.scheduleShiftDays) && options.scheduleShiftDays > 0
                ? options.scheduleShiftDays
                : 0
        };

        return fetchWithAuth(`/portal/routes/${routeId}/technician`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    },
    changeRouteDate: (routeId, scheduledDate, options = {}) => {
        const payload = {
            scheduledDate,
            mergeRoutes: Boolean(options.mergeRoutes)
        };

        return fetchWithAuth(`/portal/routes/${routeId}/date`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
    },
    changeLocationDate: (routeId, locationId, waypointId, scheduledDate, options = {}) => {
        let url = `/portal/routes/${routeId}/locations/${locationId}/date`;
        if (waypointId) {
            url += `?waypoint_id=${waypointId}`;
        }
        const payload = {
            scheduledDate,
            mergeRoutes: Boolean(options.mergeRoutes)
        };

        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    moveLocationTasks: (routeId, locationId, scheduledDate, options = {}) => {
        const payload = {
            locationId,
            scheduledDate,
            mergeRoutes: Boolean(options.mergeRoutes)
        };

        return fetchWithAuth(`/portal/routes/${routeId}/locations/move`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    createRoute: (request) => {
        return fetchWithAuth('/portal/routes', {
            method: 'POST',
            body: JSON.stringify(request)
        });
    },
    deleteRoute: (routeId) => {
        return fetchWithAuth(`/portal/routes/${routeId}`, {
            method: 'DELETE'
        });
    },
    removeTasksFromLocation: (routeId, locationId, waypointId) => {
        let url = `/portal/routes/${routeId}/locations/${locationId}/tasks-all`;
        if (waypointId) {
            url += `?waypoint_id=${waypointId}`;
        }
        return fetchWithAuth(url, {
            method: 'DELETE'
        });
    },
    createRouteTemplateFromRoute: (routeId, request = {}) => {
        return fetchWithAuth(`/portal/routes/${routeId}/create-template`, {
            method: 'POST',
            body: JSON.stringify(request)
        });
    },
    updateRoute: (routeId, request) => {
        return fetchWithAuth(`/portal/routes/${routeId}`, {
            method: 'PUT',
            body: JSON.stringify(request)
        });
    },
    getActiveLocations: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.route_id) queryParams.append('route_id', params.route_id);
        if (params.created_at_from) queryParams.append('created_at_from', params.created_at_from);
        if (params.created_at_to) queryParams.append('created_at_to', params.created_at_to);
        if (params.scheduled_date_from) queryParams.append('scheduled_date_from', params.scheduled_date_from);
        if (params.scheduled_date_to) queryParams.append('scheduled_date_to', params.scheduled_date_to);
        if (params.technician) queryParams.append('technician', params.technician);
        if (params.created_by) queryParams.append('created_by', params.created_by);
        if (params.location_count_from !== undefined) queryParams.append('location_count_from', params.location_count_from);
        if (params.location_count_to !== undefined) queryParams.append('location_count_to', params.location_count_to);
        if (params.device_count_from !== undefined) queryParams.append('device_count_from', params.device_count_from);
        if (params.device_count_to !== undefined) queryParams.append('device_count_to', params.device_count_to);
        if (params.task_count_from !== undefined) queryParams.append('task_count_from', params.task_count_from);
        if (params.task_count_to !== undefined) queryParams.append('task_count_to', params.task_count_to);
        if (params.duration_from !== undefined) queryParams.append('duration_from', params.duration_from);
        if (params.duration_to !== undefined) queryParams.append('duration_to', params.duration_to);
        if (params.started_at_from) queryParams.append('started_at_from', params.started_at_from);
        if (params.started_at_to) queryParams.append('started_at_to', params.started_at_to);
        if (params.search) queryParams.append('search', params.search);
        
        const queryString = queryParams.toString();
        const url = `/portal/routes/active-locations${queryString ? `?${queryString}` : ''}`;
        return fetchWithAuth(url, { method: 'GET' });
    },
    optimizeRoute: (routeId, startLocationId) => {
        const queryParams = new URLSearchParams();
        queryParams.append('startLocationId', startLocationId);
        return fetchWithAuth(`/portal/routes/${routeId}/optimize?${queryParams.toString()}`, { method: 'GET' });
    },
    updateLocationOrder: (routeId, locations) => {
        return fetchWithAuth(`/portal/routes/${routeId}/location-order`, {
            method: 'PUT',
            body: JSON.stringify({ locations })
        });
    },
    listHistory: (criteria) => {
        const params = prepareBaseGetListParams(criteria);
        if (criteria.showOnlyModified) {
            params.append('is_modified', criteria.showOnlyModified);
        }
        return fetchWithAuth(`/portal/routes/history?${params.toString()}`, { method: 'GET' });
    },
    getRouteTasks: (routeId, criteria) => {
        const params = prepareBaseGetListParams(criteria);
        if (criteria.all !== undefined) {
            params.append('all', criteria.all);
        }
        params.append('skipped', criteria.skipped);
        return fetchWithAuth(`/portal/routes/${routeId}/tasks?${params.toString()}`, { method: 'GET' });
    },
    getRouteEvents: (routeId, criteria) => {
        const params = prepareBaseGetListParams(criteria);
        if (criteria.taskType) {
            params.append('task_type', criteria.taskType);
        }
        params.append('skipped', criteria.skipped);
        return fetchWithAuth(`/portal/routes/${routeId}/events?${params.toString()}`, { method: 'GET' });
    }
};


